/**
 * "Team Comparison" card for the Match Hub overview: a head-to-head table of each
 * team's recent record + curated facts, one stat per row (home · label · away).
 * Renders only stats at least one side has; shows an empty state when there's none.
 * The long per-team "interesting fact" lives in MatchHubTeamFactNotesCard, not here.
 */

import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import { SectionCard } from '../SectionCard';
import { useTheme } from '../../../src/theme/useTheme';
import { buildTeamComparisonRows } from '../../../src/match/helpers/matchHubTeamFacts.helper';
import type { MatchHubMockData } from '../../../src/match/types/matchHub.types';
import { getMatchHubTeamFactsStyles } from './MatchHubTeamFactsCard.styles';
import { MATCH_HUB_TEAM_FACTS_COPY, MATCH_HUB_TEAM_FACTS_TEST_IDS } from './matchHubTeamFacts.constants';

export interface MatchHubTeamFactsCardProps {
  data: MatchHubMockData;
}

export const MatchHubTeamFactsCard: React.FC<MatchHubTeamFactsCardProps> = ({ data }) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubTeamFactsStyles(theme), [theme]);

  const rows = useMemo(
    () =>
      buildTeamComparisonRows(
        { record: data.recentRecord?.home, facts: data.teamFacts?.home ?? null },
        { record: data.recentRecord?.away, facts: data.teamFacts?.away ?? null },
      ),
    [data.recentRecord?.home, data.recentRecord?.away, data.teamFacts?.home, data.teamFacts?.away],
  );

  if (rows.length === 0) {
    return (
      <SectionCard title={MATCH_HUB_TEAM_FACTS_COPY.TITLE} testID={MATCH_HUB_TEAM_FACTS_TEST_IDS.card}>
        <Text style={styles.emptyText}>{MATCH_HUB_TEAM_FACTS_COPY.EMPTY}</Text>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={MATCH_HUB_TEAM_FACTS_COPY.TITLE} testID={MATCH_HUB_TEAM_FACTS_TEST_IDS.card}>
      <View style={styles.headerRow}>
        <Text style={[styles.teamName, styles.homeSide]} numberOfLines={1}>
          {data.home.shortName}
        </Text>
        <View style={styles.labelSpacer} />
        <Text style={[styles.teamName, styles.awaySide]} numberOfLines={1}>
          {data.away.shortName}
        </Text>
      </View>
      {rows.map((row) => (
        <View key={row.label} style={styles.row}>
          <Text style={[styles.value, styles.homeSide]} numberOfLines={2}>
            {row.home}
          </Text>
          <Text style={styles.label} numberOfLines={1}>
            {row.label}
          </Text>
          <Text style={[styles.value, styles.awaySide]} numberOfLines={2}>
            {row.away}
          </Text>
        </View>
      ))}
    </SectionCard>
  );
};
